import React, {useState, useEffect, useRef} from 'react'
import {CLDImageAsset} from '_types'
import {useMultiStyleConfig, BoxProps, StackProps, FlexProps, TextProps, Box, Heading, List, ListItem, ListProps, ListItemProps, Link} from '@chakra-ui/react'
import {IconProps, Picture, CustomStyle} from '_uiComponents'
import {usePageDesigner, useAnalytics, useIsElementInViewport} from '_customHooks'
import {updateCloudinaryPDImage, PAGE_DESIGNER_MODULES, createRedirectUrl} from '_utils'

export interface MouseoverSection {
    text?: string
    accessibilityText?: string
    link?: string
    cldImageDesktop?: CLDImageAsset
    imageDesktop?: string
}

export interface MouseoverProps {
    imgDeskDefault?: CLDImageAsset
    imgTabletDefault?: CLDImageAsset
    imgMobileDefault?: CLDImageAsset
    sections: MouseoverSection[]
    title?: string
    colorCta?: string
    fullHeightSection?: boolean
    classes?: string
    extraCSS?: string
    analyticsID?: string
    analyticsList?: string
    listingLayout?: string
    analyticsName?: string
    position: number
    adaptiveHeight?: boolean
}

const Mouseover = ({
    imgDeskDefault,
    imgTabletDefault,
    imgMobileDefault,
    sections,
    title,
    colorCta,
    fullHeightSection,
    classes,
    extraCSS,
    analyticsID,
    analyticsName,
    position,
    adaptiveHeight,
}: MouseoverProps): JSX.Element => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const styles = useMultiStyleConfig('Mouseover', {colorCta, fullHeightSection, adaptiveHeight}) as Record<
        string,
        BoxProps & StackProps & FlexProps & TextProps & IconProps
    >
    const {globalImageTransformation, CloudinaryPageDesignerCNAME, linkObject} = usePageDesigner()
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()

    const [alreadySentPromoView, setAlreadySentPromoView] = useState(false)

    const handleLinkClick = (section: MouseoverSection, index: number) => {
        analyticsEventPromoClick({
            id: analyticsID ?? 'null',
            position: String(position),
            creative: PAGE_DESIGNER_MODULES.MOUSEOVER,
            promoText: section.text ?? '',
            name: analyticsName ?? 'null',
        })
    }

    const hasDefaultImage = !!(imgMobileDefault || imgTabletDefault || imgDeskDefault)

    useEffect(() => {
        if (isVisible && !alreadySentPromoView) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position: position,
                creative: PAGE_DESIGNER_MODULES.MOUSEOVER,
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySentPromoView(true)
        }
    }, [isVisible])

    return (
        <Box data-testid="mouseover" className={`cc-pd cc-pd-mouseover ${classes ?? ''}`} {...styles.wrapper} ref={targetElement}>
            <CustomStyle css={extraCSS} />
            {/* Images layer */}
            <Box {...styles.imageStack}>
                {hasDefaultImage && (
                    <Box {...styles.imageDefault} opacity={hoveredIndex === null ? 1 : 0}>
                        <Picture
                            objectFit="cover"
                            imagesm={updateCloudinaryPDImage(
                                (imgMobileDefault || imgTabletDefault || imgDeskDefault)!,
                                globalImageTransformation,
                                CloudinaryPageDesignerCNAME,
                            )}
                            imagemd={updateCloudinaryPDImage((imgTabletDefault || imgDeskDefault)!, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                            imagelg={updateCloudinaryPDImage(imgDeskDefault!, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                            imagexl={updateCloudinaryPDImage(imgDeskDefault!, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                            alt={title ?? '#'}
                        />
                    </Box>
                )}
                {sections.map((section, index) =>
                    section.cldImageDesktop || section.imageDesktop ? (
                        <Box key={index} {...styles.imageSection} opacity={hoveredIndex === index ? 1 : 0}>
                            {section.cldImageDesktop ? (
                                <Picture
                                    objectFit="cover"
                                    imagelg={updateCloudinaryPDImage(section.cldImageDesktop, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                                    imagexl={updateCloudinaryPDImage(section.cldImageDesktop, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                                    alt={section.text ?? '#'}
                                />
                            ) : (
                                <Picture objectFit="cover" imagelg={section.imageDesktop} imagexl={section.imageDesktop} alt={section.text ?? '#'} />
                            )}
                        </Box>
                    ) : null,
                )}
            </Box>
            {/* Links overlay – centered absolutely over the image */}
            <Box {...styles.overlay}>
                {title && (
                    <Heading {...styles.title} className="cc-title">
                        {title}
                    </Heading>
                )}
                <List {...(styles.list as ListProps)} onMouseLeave={() => setHoveredIndex(null)}>
                    {sections.map((section, index) =>
                        section.link && section.text ? (
                            <ListItem
                                key={index}
                                {...(styles.listItem as ListItemProps)}
                                className="cc-mouseover-section"
                                {...(section.accessibilityText && {'aria-label': section.accessibilityText})}
                            >
                                <Link
                                    href={createRedirectUrl(section.link, linkObject.hostlink, linkObject.locale, linkObject.site)}
                                    className="cc-item"
                                    onClick={() => handleLinkClick(section, index)}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                >
                                    {section.text}
                                </Link>
                            </ListItem>
                        ) : null,
                    )}
                </List>
            </Box>
        </Box>
    )
}

export {Mouseover}
